import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedLinkWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useGetSmartLinkQuery } from '../../../../redux/api/fymAPI';
import { linkTypeForUrl } from '../../profile/linkTypeForUrl';
import { SMLSegmented, SegmentedOption } from '../controls/SMLSegmented';
import { linkTypeName } from '../blocks/linkTypeName';
import { BlockAutosave } from './useBlockAutosave';
import { formMessages } from './formMessages';
import { normalizeAddress } from './normalizeAddress';
import { LinkPageList } from './LinkPageList';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

type LinkMode = 'address' | 'page';

interface LinkBlockFormProps {
  widget: SerializedLinkWidget;
  accountId: number;
  smartLinkId: number;
  autosave: BlockAutosave;
}

/**
 * The Link block (design d2, d3): a web address or one of the artist's pages
 * (`targetSmartLinkId`; the server reads the page address on each read), and the button text.
 */
const LinkBlockFormComponent: React.FC<LinkBlockFormProps> = ({ widget, accountId, smartLinkId, autosave }) => {
  const intl = useIntl();
  const addressId = useId();
  const labelId = useId();
  const savedTarget = widget.data.smartLinkId ?? null;
  const [mode, setMode] = useState<LinkMode>(savedTarget === null ? 'address' : 'page');
  const [address, setAddress] = useState(savedTarget === null ? widget.data.url : '');
  const [label, setLabel] = useState(widget.data.label ?? '');
  const [targetId, setTargetId] = useState<number | null>(savedTarget);
  const [waitsForTarget, setWaitsForTarget] = useState(false);
  const { save, markInvalid } = autosave;
  const isBad = mode === 'address' && address.trim() !== '' && normalizeAddress(address) === null;
  const service = useMemo(() => {
    const url = normalizeAddress(address);
    return url === null ? null : linkTypeName(linkTypeForUrl(url));
  }, [address]);

  // The address of a picked page: the server resolves it again on each read.
  const { target } = useGetSmartLinkQuery(
    { accountId, smartLinkId: targetId ?? 0 },
    { skip: targetId === null, selectFromResult: ({ data }) => ({ target: data?.smartLink }) }
  );

  const commitAddress = useCallback(
    (nextAddress: string, nextLabel: string) => {
      const url = normalizeAddress(nextAddress);
      if (url === null) {
        markInvalid();
        return;
      }
      save({ url, label: nextLabel.trim() === '' ? null : nextLabel });
    },
    [markInvalid, save]
  );

  // A picked page saves once its address is known.
  useEffect(() => {
    if (!waitsForTarget || target === undefined || targetId === null) {
      return;
    }
    setWaitsForTarget(false);
    save({ url: target.shareUrl ?? '', label: label.trim() === '' ? null : label, targetSmartLinkId: targetId });
  }, [label, save, target, targetId, waitsForTarget]);

  const onMode = useCallback((next: LinkMode) => setMode(next), []);
  const onAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddress(event.target.value);
      commitAddress(event.target.value, label);
    },
    [commitAddress, label]
  );
  const onLabel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(event.target.value);
      if (mode === 'address') {
        commitAddress(address, event.target.value);
      } else if (targetId !== null) {
        setWaitsForTarget(true);
      }
    },
    [address, commitAddress, mode, targetId]
  );
  const onPickPage = useCallback((id: number) => {
    setTargetId(id);
    setWaitsForTarget(true);
  }, []);

  const modeOptions = useMemo<SegmentedOption<LinkMode>[]>(
    () => [
      { value: 'address', label: intl.formatMessage(formMessages.opensAddress) },
      { value: 'page', label: intl.formatMessage(formMessages.opensPage) },
    ],
    [intl]
  );

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.link}`}>
      <div className={styles.field}>
        <span className={styles.label}>{intl.formatMessage(formMessages.opens)}</span>
        <SMLSegmented
          options={modeOptions}
          value={mode}
          onChange={onMode}
          ariaLabel={intl.formatMessage(formMessages.opens)}
        />
      </div>
      {mode === 'address' ? (
        <div className={styles.field}>
          <label htmlFor={addressId} className={styles.label}>
            {intl.formatMessage(formMessages.address)}
          </label>
          <span className={styles.inputRow}>
            <input
              id={addressId}
              className={clsx(styles.input, isBad && styles.inputBad)}
              value={address}
              onChange={onAddress}
              inputMode="url"
              aria-invalid={isBad}
              placeholder="https://"
              data-testid="link-block-address"
            />
            {service !== null && !isBad && <span className={styles.inputEnd}>{service}</span>}
          </span>
          {isBad && <p className={styles.helpBad}>{intl.formatMessage(formMessages.addressBad)}</p>}
        </div>
      ) : (
        <div className={styles.field}>
          <span className={styles.label}>{intl.formatMessage(formMessages.page)}</span>
          <LinkPageList accountId={accountId} smartLinkId={smartLinkId} selectedId={targetId} onPick={onPickPage} />
          <p className={styles.help}>{intl.formatMessage(formMessages.pageHelp)}</p>
        </div>
      )}
      <div className={styles.field}>
        <label htmlFor={labelId} className={styles.label}>
          {intl.formatMessage(formMessages.buttonText)}
          <span className={styles.optional}>{intl.formatMessage(formMessages.optional)}</span>
        </label>
        <input id={labelId} className={styles.input} value={label} onChange={onLabel} data-testid="link-block-label" />
        <p className={styles.help}>{intl.formatMessage(formMessages.buttonTextHelp)}</p>
      </div>
    </div>
  );
};

export const LinkBlockForm = TypedMemo(LinkBlockFormComponent);
